/**
 * Kuyruğun kalıcı deposu — IndexedDB.
 *
 * Neden localStorage değil: yakalanan belge base64'e çevrildiğinde birkaç MB
 * tutar; localStorage 5 MB civarında ve senkron. Bir faturayı kaydederken
 * arayüzü dondurmak ya da kotayı doldurup sessizce kaybetmek kabul edilemez.
 *
 * Burada karar verilmez, yalnızca okunur/yazılır. Tüm kurallar queue.ts'te.
 */

import type { QueueItem } from './queue.ts';

const DB_NAME = 'tekoffice-offline';
const DB_VERSION = 1;
const STORE = 'queue';

let _db: IDBDatabase | null = null;

function hasIdb(): boolean {
  return typeof indexedDB !== 'undefined';
}

export function open(): Promise<IDBDatabase | null> {
  if (_db) return Promise.resolve(_db);
  if (!hasIdb()) return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch { resolve(null); return; }

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('profile_id', 'profile_id', { unique: false });
      }
    };
    /* Depo açılamazsa uygulama çalışmaya devam etmeli: çevrimdışı yakalama
       devre dışı kalır, mevcut akışlar bozulmaz. */
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
    req.onsuccess = () => { _db = req.result; resolve(_db); };
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function all(): Promise<QueueItem[]> {
  const db = await open();
  if (!db) return [];
  return new Promise((resolve) => {
    const req = tx(db, 'readonly').getAll();
    req.onsuccess = () => resolve((req.result as QueueItem[]) ?? []);
    req.onerror = () => resolve([]);
  });
}

export async function put(item: QueueItem): Promise<boolean> {
  const db = await open();
  if (!db) return false;
  return new Promise((resolve) => {
    const req = tx(db, 'readwrite').put(item);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
}

export async function remove(id: string): Promise<void> {
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const req = tx(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

/** Test ve çıkışta bağlantıyı bırak. */
export function close(): void {
  if (_db) { try { _db.close(); } catch { /* yoksay */ } _db = null; }
}
